<template>
  <div class="file-upload-container" >
    <div :class="{'d-flex align-items-center':withSmallTooltip}">
      <label :for="id" class="file-upload-label">{{ label }}</label>
      <div
        v-if="!withSmallTooltip"
        class="file-upload-info-box"
        v-b-tooltip.hover.html
        :title="fileFormatsTooltip"
      >
        <feather-icon icon="InfoIcon" size="14" class="info-icon"/>
        <small class="file-upload-info-text">
          <strong>{{
              tf('actions.file_upload.accepted_types_label', 'Tipos aceptados:')
            }}</strong> {{
            tf('actions.file_upload.accepted_types_value', 'Imágenes, PDF, Word, Excel.')
          }}
          <strong>{{ tf('actions.file_upload.max_label', 'Máximo:') }}</strong>
          {{ tf('actions.file_upload.max_per_document', '30 MB por documento') }} •
          {{ tf('actions.file_upload.max_documents_total', '5 documentos en total') }}
        </small>
      </div>
      <div
        v-else
        class="d-flex"
        v-b-tooltip.hover.html.left.viewport
        :title="acceptedFilesInfo"
      >
        <feather-icon icon="InfoIcon" size="18" class="info-icon mb-1 cursor-pointer mr-1"/>
      </div>
      <div class="upload-button-container">
        <b-button
          variant="primary"
          size="sm"
          :disabled="disabled || uploading || deleting"
          class="upload-button"
        >
          <feather-icon icon="UploadIcon" size="18"/>
          <input
            :id="id"
            type="file"
            @change="handleFileChange"
            multiple
            :disabled="disabled || uploading || deleting"
            class="file-input"
            :accept="onlyImages ? 'image/*' : acceptedFiles"
          />
        </b-button>
        <div v-if="uploading" class="uploading-indicator">
          <b-spinner small variant="primary"/>
          <span class="uploading-text">{{
              tf('actions.file_upload.uploading', 'Subiendo archivo(s)...')
            }}</span>
        </div>
        <div v-if="deleting" class="uploading-indicator">
          <b-spinner small variant="primary"/>
          <span class="uploading-text">{{
              tf('actions.file_upload.deleting', 'Eliminando archivo...')
            }}</span>
        </div>
      </div>
    </div>

    <div class="previews" v-if="previews.length || existingPreviews.length">
      <div
        v-for="(preview, index) in existingPreviews"
        :key="'existing-' + index"
        class="preview-container"
      >
        <div class="preview-content" :title="preview.name">
          <img
            :src="preview.url"
            class="preview-image"
            @click="viewFile(preview.url)"
            :alt="preview.name"
            v-if="preview.type === 'image'"
          />
          <div v-b-tooltip.hover.html :title="preview.name" v-else>
            <feather-icon
              class="cursor-pointer"
              icon="FileTextIcon"
              size="50"
              @click="viewFile(preview.url)"
            />
          </div>
          <button
            class="remove-button"
            @click="removeExistingFile(index)"
            :disabled="disabled || uploading || deleting"
          >
            &#10005;
          </button>
          <small class="file-name">{{ preview.name }}</small>
        </div>
      </div>
      <div
        v-for="(preview, index) in previews"
        :key="index"
        class="preview-container"
      >
        <div class="preview-content" :title="preview.name">
          <img
            :src="preview.url"
            class="preview-image"
            @click="viewFile(preview.url)"
            :alt="preview.name"
            v-if="preview.type === 'image'"
          />
          <div v-b-tooltip.hover.html :title="preview.name" v-else>
            <feather-icon
              class="cursor-pointer"
              icon="FileTextIcon"
              size="50"
              @click="viewFile(preview.url)"
            />
          </div>
          <button
            class="remove-button"
            @click="removeFile(index)"
            :disabled="disabled || uploading || deleting"
          >
            &#10005;
          </button>
          <small class="file-name">{{ preview.name }}</small>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    id: String,
    name: String,
    label: String,
    multiple: Boolean,
    disabled: {
      type: Boolean,
      default: false,
    },
    uploading: {
      type: Boolean,
      default: false,
    },
    deleting: {
      type: Boolean,
      default: false,
    },
    onUpdateFiles: Function,
    existingFiles: {
      type: Array,
      default: () => [],
    },
    withSmallTooltip: {
      type: Boolean,
      default: false
    },
    onlyImages: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      files: [],
      previews: [],
      existingPreviews: [],
      maxFileSizeMB: 30,
      maxFilesCount: 5,
    };
  },
  computed: {
    fileFormatsTooltip() {
      return this.tf('actions.file_upload.tooltip', 'Imágenes: JPG, JPEG, PNG, GIF, BMP, WEBP, SVG<br>Documentos: PDF<br>Word: DOC, DOCX, ODT, RTF<br>Excel: XLS, XLSX, CSV, ODS')
    },
    acceptedFilesInfo() {
      const acceptedTypes = this.onlyImages
        ? 'Imágenes.'
        : 'Imágenes, PDF, Word, Excel.'

      return this.tf(
        'actions.file_upload.info',
        `<strong>Máximo:</strong> 5 MB por documento • 5 documentos en total. 
        <strong>Tipos aceptados:</strong> ${acceptedTypes}`
      )
    },
    acceptedFiles() {
      return '.jpg,.jpeg,.png,.gif,.bmp,.webp,.svg,.pdf,.doc,.docx,.odt,.rtf,.xls,.xlsx,.csv,.ods,.ppt,.pptx,.odp'
    }
  },
  watch: {
    uploading(newVal, oldVal) {
      if (oldVal === true && newVal === false) {
        this.files = [];
        this.previews = [];
      }
    },
    existingFiles: {
      async handler(newVal) {
        const list = Array.isArray(newVal) ? newVal : [];
        const promises = list.map(async (file) => {
          const type = file.type || await this.getRemoteFileType(file.url);
          return {...file, type};
        });
        this.existingPreviews = await Promise.all(promises);
      },
      deep: true,
    },
  },
  async mounted() {
    const promises = this.existingFiles.map(async (file) => {
      const type = await this.getRemoteFileType(file.url);
      return {...file, type};
    });
    this.existingPreviews = await Promise.all(promises);
  },
  methods: {
    tf(key, fallback = '') {
      try {
        const out = this.$t ? this.$t(key) : key
        return typeof out === 'string' && out === key ? fallback : out
      } catch (e) {
        return fallback
      }
    },
    handleFileChange(event) {
      if (this.disabled) return;
      const newFiles = Array.from(event.target.files || []);

      const currentTotalFiles = this.files.length + this.existingPreviews.length;
      const maxAllowed = this.maxFilesCount;

      if (currentTotalFiles >= maxAllowed) {
        if (this.$toast) {
          this.$toast.error(this.$t('actions.file_upload.max_documents_error', {n: maxAllowed}) || `Máximo ${maxAllowed} documentos permitidos`);
        }
        event.target.value = "";
        return;
      }

      const maxSizeBytes = this.maxFileSizeMB * 1024 * 1024;
      const validFiles = [];
      const errors = [];

      newFiles.forEach(file => {
        if (file.size > maxSizeBytes) {
          errors.push(this.$t('actions.file_upload.max_size_error', {
            name: file.name,
            n: this.maxFileSizeMB
          }) || `${file.name}: excede el tamaño máximo de ${this.maxFileSizeMB} MB`);
          return;
        }

        const willExceedLimit = currentTotalFiles + validFiles.length >= maxAllowed;
        if (willExceedLimit) {
          errors.push(this.$t('actions.file_upload.max_documents_error', {n: maxAllowed}) || `Máximo ${maxAllowed} documentos permitidos`);
          return;
        }

        validFiles.push(file);
      });

      if (errors.length > 0) {
        if (this.$toast) {
          errors.forEach(error => this.$toast.error(error));
        }
        event.target.value = "";
      }

      validFiles.forEach(file => {
        this.files.push(file);
        this.previews.push({
          url: URL.createObjectURL(file),
          name: file.name,
          type: this.getFileType(file.name)
        });
      });

      if (validFiles.length > 0) {
        this.emitFiles();
      }

      event.target.value = "";
    },
    removeFile(index) {
      if (this.disabled) return;
      this.files.splice(index, 1);
      this.previews.splice(index, 1);
      this.emitFiles();
    },
    removeExistingFile(index) {
      if (this.disabled) return;

      // console.log("Removing existing file at index:", index);
      this.existingPreviews.splice(index, 1);

      this.emitFiles();
    },
    viewFile(url) {
      // Open in image in a new file
      //console.log("Viewing image URL:", url);
      window.open(url, "_blank");
    },
    emitFiles() {
      const existingFiles = this.existingPreviews.map((file) => ({
        url: file.url,
        name: file.name,
        raw: null, //existing files doesn't have raw file initialy
      }));

      const newFiles = this.files.map((file) => ({
        url: "",
        name: file.name,
        raw: file,
      }));

      if (this.onUpdateFiles) {
        this.onUpdateFiles({
          id: this.id,
          files: [...existingFiles, ...newFiles], // Send all files existing and news
          label: this.label,
          name: this.name,
          rawFiles: this.files, //Just raw files of new files
        });
      }
    },
    getFileType(filename) {
      if (!filename || typeof filename !== 'string') return 'file';

      const extension = filename.split('.').pop().toLowerCase();

      const types = {
        image: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'tiff', 'ico'],
        pdf: ['pdf'],
        word: ['doc', 'docx', 'rtf', 'odt'],
        excel: ['xls', 'xlsx', 'csv', 'ods'],
        ppt: ['ppt', 'pptx', 'odp'],
        video: ['mp4', 'mkv', 'avi', 'mov', 'webm', 'wmv'],
        audio: ['mp3', 'wav', 'ogg', 'm4a'],
        zip: ['zip', 'rar', '7z', 'tar', 'gz'],
        code: ['js', 'ts', 'html', 'css', 'json', 'php', 'sql', 'go', 'py']
      };

      for (const type of Object.keys(types)) {
        if (types[type].includes(extension)) {
          return type;
        }
      }

      return 'file';
    },
    async getRemoteFileType(url) {
      try {
        const response = await fetch(url, {
          method: 'GET',
        });

        if (!response.ok) return 'file';

        const mimeType = response.headers.get('Content-Type');

        if (!mimeType) return 'file';

        if (mimeType.startsWith('image/')) return 'image';
        if (mimeType.startsWith('video/')) return 'video';
        if (mimeType.startsWith('audio/')) return 'audio';

        if (mimeType === 'application/pdf') return 'pdf';

        if (mimeType.includes('word') || mimeType.includes('document')) return 'word';
        if (mimeType.includes('excel') || mimeType.includes('sheet') || mimeType.includes('csv')) return 'excel';
        if (mimeType.includes('zip') || mimeType.includes('compressed')) return 'zip';

        return 'file';
      } catch (error) {
        console.error("error resolving remote file type:", error);
        return 'file';
      }
    }
  },
};
</script>

<style>
.file-upload-container {
  margin-top: 10px;
}

.file-upload-label {
  display: block;
  line-height: 1.5;
  font-size: 0.9em;
  font-weight: bolder;
  margin-bottom: 5px;
}

.file-upload-info-box {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  background-color: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 6px;
  margin-bottom: 8px;
}

.info-icon {
  color: #0284c7;
  flex-shrink: 0;
  margin-top: 2px;
}

.file-upload-info-text {
  color: #0c4a6e;
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 0;
}

.file-upload-info-text strong {
  color: #075985;
  font-weight: 600;
}

.upload-button-container {
  display: flex;
  align-items: center;
  gap: 10px;
}

.uploading-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
}

.uploading-text {
  font-size: 0.875rem;
  color: var(--primary);
}

.upload-button {
  position: relative;
}

.file-input {
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  cursor: pointer;
}

.previews {
  display: flex;
  flex-direction: column;
  width: 110px;
}

.preview-container {
  position: relative;
  margin-right: 10px;
  margin-bottom: 10px;
}

.preview-content {
  display: block;
  position: relative;
}

.preview-image {
  max-width: 150px;
  max-height: 100px;
  cursor: pointer;
  margin-bottom: 5px;
}

.remove-button {
  position: absolute;
  top: -5px;
  right: -3px;
  background-color: red;
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  width: 20px;
  height: 20px;
  font-size: 12px;
  line-height: 22px;
  padding: 0;
}

.remove-button:disabled {
  background-color: gray;
  cursor: not-allowed;
}

small.file-name {
  display: block;
  position: absolute;
  top: 25px;
  width: 170px;
  right: -170px;
}
</style>
